<template>
  <div class="pricing-wrapper">
    <Card class="card">
      <template #title>
        <div class="title-block">
          <h2>VirtualGarage Pricing Calculator</h2>
          <p>Calculate your monthly price based on your workshop size and add-ons.</p>
        </div>
      </template>

      <template #content>
        <div class="grid">
          <div class="field">
            <label>Staff members</label>
            <InputNumber
                v-model="staff"
                :min="1"
                showButtons
                buttonLayout="horizontal"
                decrementButtonClass="p-button-secondary"
                incrementButtonClass="p-button-secondary"
            />
            <small>Base includes 1 staff member</small>
          </div>

          <div class="field">
            <label>Locations</label>
            <InputNumber
                v-model="locations"
                :min="1"
                showButtons
                buttonLayout="horizontal"
                decrementButtonClass="p-button-secondary"
                incrementButtonClass="p-button-secondary"
            />
            <small>Base includes 1 location</small>
          </div>

          <div class="field">
            <label>Outgoing email accounts</label>
            <InputNumber
                v-model="outgoingEmails"
                :min="1"
                showButtons
                buttonLayout="horizontal"
                decrementButtonClass="p-button-secondary"
                incrementButtonClass="p-button-secondary"
            />
            <small>Base includes 1 outgoing email account</small>
          </div>

          <div class="field">
            <label>Incoming email accounts</label>
            <InputNumber
                v-model="incomingEmails"
                :min="0"
                showButtons
                buttonLayout="horizontal"
                decrementButtonClass="p-button-secondary"
                incrementButtonClass="p-button-secondary"
            />
            <small>Optional: connect extra inboxes</small>
          </div>
        </div>

        <hr />

<!--        <div class="section-title">Modules</div>-->
        <div class="section-title">Add Capabilities</div>

        <div class="modules">
          <!-- Customer Communication Pack -->
          <div class="module-row">
            <div class="module-left">
              <div class="module-title">Customer Communication Pack</div>
              <small>Customer emails & SMS workflows</small>
            </div>
            <div class="module-right">
              <div class="loc-slot" :class="{ 'loc-slot--hidden': !showLocInput('customerCommunicationPack') }">
                <span class="loc-label">Locations</span>
                <InputNumber
                    v-model="moduleEnabledLocations.customerCommunicationPack"
                    :min="1"
                    :max="locations"
                    showButtons
                    class="loc-input"
                />
              </div>
              <span class="price">€{{ moneyStr(PRICES.customerCommunicationPack) }}</span>
              <InputSwitch v-model="modules.customerCommunicationPack" />
            </div>
          </div>

          <!-- Price Request Form -->
          <div class="module-row">
            <div class="module-left">
              <div class="module-title">Price Request Form</div>
              <small>Let customers request estimates online</small>
            </div>
            <div class="module-right">
              <div class="loc-slot" :class="{ 'loc-slot--hidden': !showLocInput('priceRequestForm') }">
                <span class="loc-label">Locations</span>
                <InputNumber
                    v-model="moduleEnabledLocations.priceRequestForm"
                    :min="1"
                    :max="locations"
                    showButtons
                    class="loc-input"
                />
              </div>
              <span class="price">€{{ moneyStr(PRICES.priceRequestForm) }}</span>
              <InputSwitch v-model="modules.priceRequestForm" />
            </div>
          </div>

          <!-- Customer Self-Service Link -->
          <div class="module-row">
            <div class="module-left">
              <div class="module-title">Customer Self-Service Link</div>
              <small>Share order progress and documents</small>
            </div>
            <div class="module-right">
              <div class="loc-slot" :class="{ 'loc-slot--hidden': !showLocInput('customerSelfServiceLink') }">
                <span class="loc-label">Locations</span>
                <InputNumber
                    v-model="moduleEnabledLocations.customerSelfServiceLink"
                    :min="1"
                    :max="locations"
                    showButtons
                    class="loc-input"
                />
              </div>
              <span class="price">€{{ moneyStr(PRICES.customerSelfServiceLink) }}</span>
              <InputSwitch v-model="modules.customerSelfServiceLink" />
            </div>
          </div>

          <!-- Service Reminder -->
          <div class="module-row">
            <div class="module-left">
              <div class="module-title">Service Reminder module</div>
              <small>Automated service reminders</small>
            </div>
            <div class="module-right">
              <div class="loc-slot" :class="{ 'loc-slot--hidden': !showLocInput('serviceReminder') }">
                <span class="loc-label">Locations</span>
                <InputNumber
                    v-model="moduleEnabledLocations.serviceReminder"
                    :min="1"
                    :max="locations"
                    showButtons
                    class="loc-input"
                />
              </div>
              <span class="price">€{{ moneyStr(PRICES.serviceReminder) }}</span>
              <InputSwitch v-model="modules.serviceReminder" />
            </div>
          </div>

          <!-- Accounting Pack -->
          <div class="module-row">
            <div class="module-left">
              <div class="module-title">Accounting Pack</div>
              <small>Advanced financial tools</small>
            </div>
            <div class="module-right">
              <div class="loc-slot" :class="{ 'loc-slot--hidden': !showLocInput('accountingPack') }">
                <span class="loc-label">Locations</span>
                <InputNumber
                    v-model="moduleEnabledLocations.accountingPack"
                    :min="1"
                    :max="locations"
                    showButtons
                    class="loc-input"
                />
              </div>
              <span class="price">€{{ moneyStr(PRICES.accountingPack) }}</span>
              <InputSwitch v-model="modules.accountingPack" />
            </div>
          </div>

<!--          Dont delete!!-->
<!--           Export to Accounting-->
          <div class="module-row" :class="{ disabled: !modules.accountingPack }">
            <div class="module-left">
              <div class="module-title">Export to Accounting</div>
              <small>
                Requires Accounting Pack
                <span v-if="!modules.accountingPack" class="hint">(enable Accounting Pack first)</span>
              </small>
            </div>
            <div class="module-right">
              <div class="loc-slot" :class="{ 'loc-slot--hidden': !showLocInput('exportToAccounting') }">
                <span class="loc-label">Locations</span>
                <InputNumber
                    v-model="moduleEnabledLocations.exportToAccounting"
                    :min="1"
                    :max="locations"
                    showButtons
                    class="loc-input"
                    :disabled="!modules.accountingPack"
                />
              </div>
              <span class="price">€{{ moneyStr(PRICES.exportToAccounting) }}</span>
              <InputSwitch v-model="modules.exportToAccounting" :disabled="!modules.accountingPack" />
            </div>
          </div>

          <!-- Payroll Pack -->
          <div class="module-row">
            <div class="module-left">
              <div class="module-title">Staff Compensation / Payroll Pack</div>
              <small>Payroll calculations and staff performance</small>
            </div>
            <div class="module-right">
              <div class="loc-slot" :class="{ 'loc-slot--hidden': !showLocInput('payrollPack') }">
                <span class="loc-label">Locations</span>
                <InputNumber
                    v-model="moduleEnabledLocations.payrollPack"
                    :min="1"
                    :max="locations"
                    showButtons
                    class="loc-input"
                />
              </div>
              <span class="price">€{{ moneyStr(PRICES.payrollPack) }}</span>
              <InputSwitch v-model="modules.payrollPack" />
            </div>
          </div>
        </div>

        <hr />

        <div class="result-box">
          <div class="result-label">Estimated Monthly Price</div>
          <div><span class="result-price">€{{ moneyStr(total) }}</span> <span>(€{{moneyStr(total / 23) }} per day)</span></div>
          <div class="result-sub">VAT not included</div>
        </div>

        <!-- Breakdown -->
        <div class="breakdown">
          <div class="break-row">
            <span>Base (1 location, 1 staff, 1 outgoing, 1 incoming)</span>
            <span>€{{ moneyStr(BASE_PRICE) }}</span>
          </div>

          <div class="break-row" v-if="extraStaffCount">
            <span>Additional staff × {{ extraStaffCount }}</span>
            <span>€{{ moneyStr(extraStaffCount * STAFF_PRICE) }}</span>
          </div>

          <div class="break-row" v-if="extraLocationsCount">
            <span>Additional locations × {{ extraLocationsCount }}</span>
            <span>€{{ moneyStr(extraLocationsCount * LOCATION_PRICE) }}</span>
          </div>

          <div class="break-row" v-if="extraOutgoingEmailsCount">
            <span>Outgoing email accounts × {{ extraOutgoingEmailsCount }}</span>
            <span>€{{ moneyStr(extraOutgoingEmailsCount * OUTGOING_EMAIL_PRICE) }}</span>
          </div>

          <div class="break-row" v-if="extraIncomingEmailsCount">
            <span>Incoming email accounts × {{ extraIncomingEmailsCount }}</span>
            <span>€{{ moneyStr(extraIncomingEmailsCount * INCOMING_EMAIL_PRICE) }}</span>
          </div>

          <div class="break-group" v-if="selectedModulesList.length">
            <div class="break-row group-title">
              <span>Selected modules</span>
              <span>€{{ moneyStr(modulesSubtotal) }}</span>
            </div>

            <div class="break-row module-item" v-for="m in selectedModulesList" :key="m.key">
              <span class="module-name">
                • {{ m.title }}
                <span v-if="m.enabledLocations > 1"> ({{ m.enabledLocations }}/{{ locations }} loc)</span>
              </span>
              <span>€{{ moneyStr(m.totalPrice) }}</span>
            </div>

            <div class="break-row discount" v-if="modulesDiscountAmount">
              <span>Bundle discount ({{ PACKS_DISCOUNT_PCT }}% for {{ PACKS_DISCOUNT_THRESHOLD }}+ packs)</span>
              <span>-€{{ moneyStr(modulesDiscountAmount) }}</span>
            </div>

            <div class="break-row module-total">
              <span>Modules total</span>
              <span>€{{ moneyStr(modulesTotal) }}</span>
            </div>
          </div>

          <div class="break-total">
            <span>Total</span>
            <span>€{{ moneyStr(total) }}</span>
          </div>
        </div>

        <Button label="Start Free Trial" class="p-button-lg p-button-raised full-width" />
      </template>
    </Card>
  </div>
</template>

<script>
export default {
  name: 'VGPricingCalculator',

  data() {
    return {
      // Base + scaling
      BASE_PRICE: 40,
      INCLUDED_STAFF: 1,
      STAFF_PRICE: 8,
      LOCATION_PRICE: 25,

      // Emails
      INCLUDED_OUTGOING_EMAILS: 1,
      INCLUDED_INCOMING_EMAILS: 0,
      OUTGOING_EMAIL_PRICE: 2,
      INCOMING_EMAIL_PRICE: 5,

      // Discount rule (packs/modules only)
      PACKS_DISCOUNT_THRESHOLD: 4,
      PACKS_DISCOUNT_PCT: 10,

      // Module prices (price for 1 location)
      PRICES: {
        customerCommunicationPack: 15,
        priceRequestForm: 10,
        customerSelfServiceLink: 15,
        accountingPack: 20,
        payrollPack: 10,
        exportToAccounting: 15,
        serviceReminder: 12,
      },

      // Inputs
      staff: 1,
      locations: 1,
      outgoingEmails: 1,
      incomingEmails: 0,

      // Modules
      modules: {
        customerCommunicationPack: false,
        priceRequestForm: false,
        customerSelfServiceLink: false,
        serviceReminder: false,
        accountingPack: false,
        exportToAccounting: false,
        payrollPack: false
      },

      // Enabled locations per module (1..locations)
      moduleEnabledLocations: {
        customerCommunicationPack: 1,
        priceRequestForm: 1,
        customerSelfServiceLink: 1,
        serviceReminder: 1,
        accountingPack: 1,
        exportToAccounting: 1,
        payrollPack: 1
      }
    }
  },

  methods: {
    showLocInput(key) {
      return this.locations > 1 && !!this.modules[key]
    },

    money(v) {
      const n = Number(v) || 0
      return Math.round((n + Number.EPSILON) * 100) / 100
    },

    moneyStr(v) {
      const n = this.money(v)
      return Number.isInteger(n) ? String(n) : n.toFixed(2)
    },

    moduleTotalPrice(key) {
      const base = this.money(this.PRICES[key] || 0)
      const enabled = Math.max(1, Math.min(this.locations, +this.moduleEnabledLocations[key] || 1))
      // per location 100%
      return this.money(base * enabled)
    }
  },

  watch: {
    'modules.accountingPack'(val) {
      if (!val && this.modules.exportToAccounting) this.modules.exportToAccounting = false
    },

    locations(val) {
      const max = Math.max(1, +val || 1)
      Object.keys(this.moduleEnabledLocations).forEach((k) => {
        const cur = +this.moduleEnabledLocations[k] || 1
        this.moduleEnabledLocations[k] = Math.min(max, Math.max(1, cur))
      })
    },

    modules: {
      deep: true,
      handler() {
        // при включении модуля гарантируем корректный enabledLocations
        const max = Math.max(1, +this.locations || 1)
        Object.keys(this.modules).forEach((k) => {
          if (this.modules[k]) {
            const cur = +this.moduleEnabledLocations[k] || 1
            this.moduleEnabledLocations[k] = Math.min(max, Math.max(1, cur))
          }
        })
      }
    }
  },

  computed: {
    extraStaffCount() {
      return Math.max(0, this.staff - this.INCLUDED_STAFF)
    },

    extraLocationsCount() {
      return Math.max(0, this.locations - 1)
    },

    extraOutgoingEmailsCount() {
      return Math.max(0, this.outgoingEmails - this.INCLUDED_OUTGOING_EMAILS)
    },

    extraIncomingEmailsCount() {
      return Math.max(0, this.incomingEmails - this.INCLUDED_INCOMING_EMAILS)
    },

    selectedModulesList() {
      const defs = [
        { key: 'customerCommunicationPack', title: 'Customer Communication Pack' },
        { key: 'priceRequestForm', title: 'Price Request Form' },
        { key: 'customerSelfServiceLink', title: 'Customer Self-Service Link' },
        { key: 'serviceReminder', title: 'Service Reminder module' },
        { key: 'accountingPack', title: 'Accounting Pack' },
        { key: 'exportToAccounting', title: 'Export to Accounting' },
        { key: 'payrollPack', title: 'Staff Compensation / Payroll Pack' },
      ]

      return defs
          .filter(d => !!this.modules[d.key])
          .map(d => {
            const enabledLocations = Math.max(1, Math.min(this.locations, +this.moduleEnabledLocations[d.key] || 1))
            return {
              key: d.key,
              title: d.title,
              enabledLocations,
              totalPrice: this.moduleTotalPrice(d.key)
            }
          })
    },

    selectedPaidPacksCount() {
      return this.selectedModulesList.length
    },

    modulesSubtotal() {
      const sum = this.selectedModulesList.reduce((s, m) => s + (Number(m.totalPrice) || 0), 0)
      return this.money(sum)
    },

    modulesDiscountAmount() {
      if (this.selectedPaidPacksCount < this.PACKS_DISCOUNT_THRESHOLD) return 0
      return this.money(this.modulesSubtotal * (this.PACKS_DISCOUNT_PCT / 100))
    },

    modulesTotal() {
      return this.money(this.modulesSubtotal - this.modulesDiscountAmount)
    },

    total() {
      const sum =
          this.money(this.BASE_PRICE) +
          this.money(this.extraStaffCount * this.STAFF_PRICE) +
          this.money(this.extraLocationsCount * this.LOCATION_PRICE) +
          this.money(this.extraOutgoingEmailsCount * this.OUTGOING_EMAIL_PRICE) +
          this.money(this.extraIncomingEmailsCount * this.INCOMING_EMAIL_PRICE) +
          this.money(this.modulesTotal)

      return this.money(sum)
    }
  }
}
</script>

<style scoped>
.pricing-wrapper {
  display: flex;
  justify-content: center;
  background: var(--surface-ground);
}

.card {
  width: 100%;
  max-width: 860px;
  //border-radius: 16px;
}

.title-block h2 {
  margin: 0;
  font-weight: 500;
}

.title-block p {
  margin-top: 10px;
  color: var(--text-color-secondary);
  font-weight: 300;
}

.section-title {
  font-weight: 700;
  margin: 0.25rem 0 1rem;
}

.grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.field small {
  color: var(--text-color-secondary);
}

.modules {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.module-row {
  padding: 6px;
  border: 1px solid var(--surface-border);
  border-radius: 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
}

.module-row.disabled {
  opacity: 0.6;
}

.module-title {
  font-weight: 600;
}

.module-right {
  display: grid;
  grid-template-columns: 160px 30px 40px;
  align-items: center;
  gap: 12px;
  justify-items: end;
}

.price {
  font-weight: 600;
  color: var(--text-color);
  justify-self: end;
}

.hint {
  color: var(--text-color-secondary);
  margin-left: 0.25rem;
}

.result-box {
  text-align: center;
  padding: 10px;
  background: var(--primary-color);
  color: #fff;
  border-radius: 10px;
}

.result-price {
  font-size: 2.6rem;
  font-weight: 800;
  margin-top: 0.35rem;
}

.breakdown {
  margin-top: 1.25rem;
  font-size: 0.95rem;
  color: var(--text-color);
  border: 1px solid var(--surface-border);
  border-radius: 12px;
  overflow: hidden;
}

.break-row,
.break-total {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
}

.break-row {
  border-top: 1px solid var(--surface-border);
}

.break-row:first-child {
  border-top: 0;
}

.break-group .group-title {
  font-weight: 800;
}

.break-row.module-item {
  color: var(--text-color-secondary);
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
}

.module-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.break-row.discount {
  color: var(--green-600);
  font-weight: 800;
}

.break-row.module-total {
  font-weight: 800;
}

.break-total {
  font-weight: 900;
  background: var(--surface-50);
  border-top: 1px solid var(--surface-border);
}

.full-width {
  width: 100%;
  margin-top: 1.75rem;
}

/* ---------- Locations slot (always reserved space) ---------- */
.loc-slot {
  width: 120px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 2px;
}

.loc-slot--hidden {
  visibility: hidden; /* space stays, content hidden */
}

.loc-label {
  color: var(--text-color-secondary);
  font-size: 0.85rem;
  white-space: nowrap;
}

.loc-input {
  width: 92px;
}

:deep(.loc-input .p-inputnumber-input) {
  width: 52px;
  text-align: center;
  padding-left: 8px;
  padding-right: 8px;
}

:deep(.loc-input .p-inputnumber-button) {
  width: 2rem;
}

:deep(.loc-input .p-inputnumber-button-icon) {
  font-size: 0.85rem;
}
</style>